import type { ChartConfig } from '@herit-fe/heritage-ui';
import {
  Area,
  Bar,
  Line,
  Rectangle,
  useActiveTooltipDataPoints,
  type BarShapeProps,
} from 'recharts';

import type { ChartSeries, YAxisOptions } from './types';

/** 사각형 코너 반경 — 스칼라(4코너 동일) 또는 `[topLeft, topRight, bottomRight, bottomLeft]`. */
type BarRadius = number | [number, number, number, number];

/** 막대 상단(스택 가시 최상단) 라운딩 반경(px) — 세로=위 두 코너, 가로=오른쪽 두 코너. */
const BAR_TOP_RADIUS = 4;
const V_TOP_RADIUS: BarRadius = [BAR_TOP_RADIUS, BAR_TOP_RADIUS, 0, 0];
const H_END_RADIUS: BarRadius = [0, BAR_TOP_RADIUS, BAR_TOP_RADIUS, 0];

/** recharts wide row — `{ x: 카테고리, [seriesKey]: 값 }`. */
export type Row = Record<string, string | number | null>;

export const gradientId = (key: string) => `viz-grad-${key}`;
/** 색 미지정 시 순환 폴백(전역 테마 토큰). */
export const fallbackColor = (i: number) => `var(--chart-${(i % 5) + 1})`;

/**
 * 시리즈 key → **CSS custom property 안전 키**. 색은 `ChartContainer` 가 `--color-{key}` 선언으로 주입하고
 * 시리즈/툴팁이 `var(--color-{key})` 로 읽으므로, key 에 CSS ident 밖 문자(`:` 등)가 있으면 **선언도 `var()` 도
 * 무효** → stroke/fill 이 통째로 날아가 차트가 빈 축만 남는다(계측 = `deviceId::instanceId` 복합키 사례).
 * 안전 문자([A-Za-z0-9_-]) 외는 코드포인트로 이스케이프 — 서로 다른 key 가 같은 변수로 뭉치지 않게 유일성 보존.
 */
export const colorVarKey = (key: string) =>
  key.replace(/[^a-zA-Z0-9_-]/g, (c) => `_${c.codePointAt(0)?.toString(16) ?? ''}_`);

/** series → heritage `ChartConfig`(라벨·색 CSS var 주입 소스). key 는 CSS 안전형(`colorVarKey`). */
export function buildConfig(series: ChartSeries[]): ChartConfig {
  return Object.fromEntries(
    series.map((s, i) => [
      colorVarKey(s.key),
      { label: s.label, color: s.color ?? fallbackColor(i) },
    ]),
  );
}

/** ChartModel(long) → recharts wide rows. `xLabels[t]` 와 `series.values[t]` 정렬. */
export function toRows(xLabels: string[], series: ChartSeries[]): Row[] {
  return xLabels.map((label, t) => {
    const row: Row = { x: label };
    for (const s of series) row[s.key] = s.values[t] ?? null;
    return row;
  });
}

/** 막대 세로 그라데이션 defs(`type:'bar' && gradient` 인 시리즈만). */
export function GradientDefs({ series }: { series: ChartSeries[] }) {
  const gradientSeries = series.filter(
    (s): s is ChartSeries & { gradient: [string, string] } =>
      s.type === 'bar' && Array.isArray(s.gradient),
  );
  if (gradientSeries.length === 0) return null;
  return (
    <defs>
      {gradientSeries.map((s) => (
        <linearGradient key={s.key} id={gradientId(s.key)} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={s.gradient[0]} />
          <stop offset="100%" stopColor={s.gradient[1]} />
        </linearGradient>
      ))}
    </defs>
  );
}

/**
 * 활성 카테고리 강조 막대 — **recharts store 를 막대 자신이 구독**해 dim 을 계산한다.
 *
 * 상위(`CartesianPlot`)에 `activeIndex` state 를 두면 포인터가 움직일 때마다 차트 전체가 다시 렌더된다.
 * 데스크탑에서는 비용일 뿐이지만 **터치 스크럽 중에는 치명적**이다 — 재렌더가 터치 타깃을 흔들어 touchmove
 * 스트림이 끊기고 툴팁이 첫 위치에 얼어붙는다(bar 에서만 드래그가 죽던 원인. line 은 activeIndex 를 안 써서
 * 멀쩡했다). 여기서 구독하면 갱신 범위가 **막대 엘리먼트로 국한**되고 상위는 렌더되지 않는다.
 *
 * 판정은 index 가 아니라 **행 객체 동일성**으로 한다(recharts `ActivePoints` 와 같은 방식) — 0높이 막대 등이
 * 걸러져 렌더 index 가 밀려도 어긋나지 않는다.
 */
function ActiveAwareBarRectangle({
  row,
  dim,
  ...rectProps
}: BarShapeProps & { row: Row | undefined; dim: boolean }) {
  const activeRows = useActiveTooltipDataPoints<Row>();
  const isDimmed =
    dim && row != null && activeRows != null && activeRows.length > 0 && !activeRows.includes(row);
  return <Rectangle {...rectProps} fillOpacity={isDimmed ? 0.7 : 1} />;
}

/** `renderSeries` 옵션 — plot 이 방향·호버·라운딩 컨텍스트를 주입. */
export interface RenderSeriesOptions {
  /** 이중축 라우팅 — 'left'|'right'|undefined(단일축). */
  yAxisId: 'left' | 'right' | undefined;
  maxBarSize: number | undefined;
  /** 차트 행 데이터 — 막대가 자기 행 객체로 활성 여부를 판정(`ActiveAwareBarRectangle`). */
  rows: Row[];
  /** 활성 카테고리 강조(비활성 막대 dim) 사용 여부 = `highlightOnHover`. */
  dim: boolean;
  /** 가로 막대 여부 — 상단 라운딩 코너 방향(세로=위·가로=오른쪽). */
  horizontal: boolean;
  /**
   * 카테고리별 상단 라운딩 대상 시리즈 key 집합(`topRoundedKeys`) — 스택의 가시 최상단만 담긴다.
   * 이 집합에 `s.key` 가 있는 카테고리의 막대만 상단을 굴린다.
   */
  roundedKeys: ReadonlyArray<ReadonlySet<string>>;
}

/**
 * 시리즈 1개 → recharts 엘리먼트(bar/line/area). 색은 `var(--color-{key})`(ChartContainer config 주입) →
 * **ChartContainer 안에서만 색이 해석**된다(스크롤/비스크롤 공통 렌더 경로 — 시리즈 종류 유실 없음).
 */
export function renderSeries(s: ChartSeries, opts: RenderSeriesOptions) {
  const { yAxisId, maxBarSize, rows, dim, horizontal, roundedKeys } = opts;
  // 색 참조는 CSS 안전 키 — `buildConfig` 가 같은 변환으로 선언한 `--color-{안전키}` 와 짝을 맞춘다.
  const colorVar = `var(--color-${colorVarKey(s.key)})`;
  const fill = s.type === 'bar' && s.gradient ? `url(#${gradientId(s.key)})` : colorVar;

  if (s.type === 'bar') {
    // per-bar 커스터마이즈 = recharts v3 `shape` 렌더 프롭(구 `Cell` 은 deprecated·v4 제거 예정). 기본 shape 과
    // 동일한 `Rectangle` 에 (1) 상단 라운딩(스택 가시 최상단만·상시) (2) 활성 카테고리 강조만 얹는다 — 색·좌표·
    // 애니메이션 props 는 recharts 가 준 그대로 통과(기본 렌더 경로 동일). 강조는 **엘리먼트를 반환**해
    // `ActiveAwareBarRectangle` 안에서 store 를 구독한다(상위 재렌더 회피 — 그 컴포넌트 주석 참조).
    // 카테고리 index 는 `originalDataIndex`(필터 전 데이터 슬라이스 기준) — 0높이 막대 등이 걸러져도 행이
    // 어긋나지 않는다(렌더 index 는 필터 후 기준이라 부적합).
    const barShape = (shapeProps: BarShapeProps) => {
      const i = shapeProps.originalDataIndex;
      const rounded = roundedKeys[i]?.has(s.key);
      return (
        <ActiveAwareBarRectangle
          {...shapeProps}
          radius={rounded ? (horizontal ? H_END_RADIUS : V_TOP_RADIUS) : undefined}
          row={rows[i]}
          dim={dim}
        />
      );
    };
    return (
      <Bar
        key={s.key}
        yAxisId={yAxisId}
        dataKey={s.key}
        name={s.label}
        stackId={s.stackId}
        fill={fill}
        maxBarSize={maxBarSize}
        isAnimationActive={true}
        shape={barShape}
      />
    );
  }
  if (s.type === 'area') {
    return (
      <Area
        key={s.key}
        yAxisId={yAxisId}
        dataKey={s.key}
        name={s.label}
        stackId={s.stackId}
        type="monotone"
        // 윤곽선은 기본 표시 — `showStroke:false` 면 채움만 남겨 다른 시리즈 뒤 배경 형상으로 쓴다.
        stroke={s.showStroke === false ? 'none' : colorVar}
        fill={fill}
        fillOpacity={s.fillOpacity ?? 0.2}
        dot={false}
        isAnimationActive={true}
      />
    );
  }
  return (
    <Line
      key={s.key}
      yAxisId={yAxisId}
      dataKey={s.key}
      name={s.label}
      type="monotone"
      stroke={colorVar}
      strokeWidth={2}
      // dot 기본 표시 — connectNulls=false 라 결측(null)이 선을 끊는데, 양쪽 null 로 고립된 단일 지점은 점이 없으면
      // 이을 상대가 없어 사라진다(예: 일사량 산발 결측). dot 로 고립점도 표기. 단 채널 다수(스트링 12~24)면
      // `showDots:false` 로 끄고 hover activeDot(recharts 기본)만 남겨 노이즈 제거.
      dot={s.showDots ?? true}
      isAnimationActive={true}
    />
  );
}

/**
 * 카테고리별 **상단 라운딩 대상** 시리즈 key 집합. 스택(같은 stackId)에서 `값 > 0` 인 **가장 위 시리즈**(렌더
 * 순서상 마지막 = 시각적 최상단)만 굴린다. 최상단 값이 0이면 그 아래 첫 비영(非零) 시리즈로 자동으로 내려간다
 * (0 세그먼트는 recharts 가 렌더 자체를 생략하므로 라운딩도 다음 것으로 승계). 비스택(그룹/단일) 막대는
 * 각자가 유일 멤버라 `값 > 0` 이면 라운딩. `series` 배열 순서 = 스택 쌓임 순서(첫=바닥).
 *
 * 반환은 카테고리 index 로 접근하는 배열(각 원소 = 그 카테고리에서 굴릴 key 들). 음수 값은 대상 아님(baseline
 * 아래로 자라 상단 라운딩 개념이 안 맞음 — 필요 시 별도 처리).
 */
export function topRoundedKeys(series: ChartSeries[], rowCount: number): Array<Set<string>> {
  const groups = new Map<string, ChartSeries[]>();
  for (const s of series) {
    if (s.type !== 'bar') continue;
    const gid = s.stackId ?? ` ${s.key}`; // 비스택은 key 별 독립 그룹(자기 자신이 최상단)
    const arr = groups.get(gid);
    if (arr) arr.push(s);
    else groups.set(gid, [s]);
  }
  const result = Array.from({ length: rowCount }, () => new Set<string>());
  for (const group of groups.values()) {
    for (let t = 0; t < rowCount; t++) {
      let topKey: string | null = null;
      for (const s of group) {
        const v = s.values[t];
        if (v != null && v > 0) topKey = s.key; // 마지막 비영 = 가시 최상단
      }
      if (topKey) result[t]?.add(topKey);
    }
  }
  return result;
}

// ── 값축·밴드 기본값 계산(defaults 내장 — 소비처가 sizing 상수를 넘기지 않아도 되게) ──────────────

/** 카테고리당 병렬 막대 그룹 수 — 같은 stackId 는 1(누적), 나머지는 각 1(그룹). */
export function barGroups(series: ChartSeries[]): number {
  const stacks = new Set(series.filter((s) => s.type === 'bar').map((s) => s.stackId ?? s.key));
  return Math.max(1, stacks.size);
}

/** 막대 두께 상한 기본값 — 그룹(병렬 다수)이면 얇게. 단일/누적 34px 은 카드 폭 기준 실측치. */
export function defaultMaxBarSize(series: ChartSeries[]): number {
  return barGroups(series) <= 1 ? 50 : 14;
}

/** 카테고리 1행(밴드) 크기 기본값(px) — 병렬 막대 수에 비례. */
export function defaultBandSize(series: ChartSeries[]): number {
  const g = barGroups(series);
  return g <= 1 ? 44 : g * 16 + 8;
}

/**
 * 값축 raw max — stackId 별 행 합의 최댓값(비누적은 개별 최댓값). **가시 series 기준**(범례 토글 반영). 값이
 * 없으면 0(빈 상태) — nice-round 가 0→기본 축으로 처리하므로 여기선 clamp 하지 않는다.
 */
export function computeValueMax(data: Row[], series: ChartSeries[]): number {
  // 좌축 series 만(우축은 별도 max). 막대·누적영역 = stackId 합산, 순수 라인/비누적 = 개별(각 key) 최댓값.
  // 이전엔 막대만 집계 → 순수 라인 차트(계측·PV)는 max=0 → 도메인 [0,1] 로 붕괴하고 데이터가 축 위로 넘쳤다.
  const left = series.filter((s) => s.axis !== 'right');
  let max = 0;
  for (const row of data) {
    const byStack = new Map<string, number>();
    for (const s of left) {
      const v = Number(row[s.key]) || 0;
      const k = s.stackId ?? s.key;
      byStack.set(k, (byStack.get(k) ?? 0) + v);
    }
    for (const total of byStack.values()) max = Math.max(max, total);
  }
  return max;
}

/** "nice" step(1/2/2.5/5 × 10ᵏ) — 대략 간격을 보기 좋은 값으로 스냅. */
function niceStep(rough: number): number {
  if (rough <= 0) return 1;
  const k = 10 ** Math.floor(Math.log10(rough));
  const f = rough / k;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nice * k;
}

/**
 * 값축 도메인·tick — **데이터 max 기준**으로 nice-round(고정 0~100 아님). 0 부터 nice step 으로 올려 max 를
 * 덮는 최소 nice 상한까지. 예: max 170 → [0,200] tick 0/50/100/150/200, max 70 → [0,80] tick 0/20/…/80.
 */
export function niceValueAxis(
  max: number,
  count = 5,
  integer = false,
): { domain: [number, number]; ticks: number[] } {
  // 데이터 없음(가시 series 전무·전부 0) → 소수 tick(0.25…) 뭉갬 없이 깔끔한 기본 축(0~1 정수 baseline).
  if (max <= 0) return { domain: [0, 1], ticks: [0, 1] };
  let step = niceStep(max / Math.max(1, count - 1));
  // 정수 축(개수 등) — step 을 정수(≥1)로 스냅(2.5→2, 0.5→1)해 tick 위치를 정수화. 안 하면 소수 위치가
  // formatInteger(0소수)에서 라벨 중복("1","1")·gridline 이 소수 count 에 찍힘.
  if (integer) step = step < 1 ? 1 : Math.floor(step);
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let t = 0; t <= niceMax + step / 2; t += step) ticks.push(Math.round(t * 1e6) / 1e6);
  return { domain: [0, niceMax], ticks };
}

/** 주어진 도메인의 등간격 tick(count 개, 양끝 포함) — 명시 도메인 폴백용. */
export function valueTicks(domain: [number, number], count = 5): number[] {
  const n = Math.max(1, count - 1);
  return Array.from({ length: count }, (_, i) => domain[0] + ((domain[1] - domain[0]) * i) / n);
}

/** 해석된 값축 — 도메인·틱·포맷·단위(모든 렌더 경로가 이걸 그대로 recharts/sticky 에 적용). */
export interface ResolvedValueAxis {
  domain: [number, number];
  ticks: number[];
  tickFormatter?: (value: number) => string;
  unit?: string;
}

/**
 * 값축 단일 해석 — **가시 series 의 `rawMax`** 로부터 도메인·틱·포맷·단위를 한 번에 산출(세로·가로·스크롤 전
 * 경로 공용). 우선순위: `left.resolve(rawMax)`(동적 — 토글마다 재계산) > `valueDomain`(정적 도메인) >
 * `niceValueAxis(rawMax)`(데이터 max nice-round). `resolve` 가 일부만 주면 나머지는 이 폴백으로 채운다.
 * `tickFormatter`·`unit` 은 `resolve` 반환 > static(`left.tickFormatter`/`left.unit`) 순.
 */
export function resolveValueAxis(
  rawMax: number,
  left: YAxisOptions | undefined,
  valueDomain: [number, number] | undefined,
): ResolvedValueAxis {
  const r = left?.resolve?.(rawMax);
  let domain: [number, number];
  let ticks: number[];
  if (r?.domain) {
    domain = r.domain;
    ticks = r.ticks ?? valueTicks(r.domain);
  } else if (valueDomain) {
    domain = valueDomain;
    ticks = valueTicks(valueDomain);
  } else {
    ({ domain, ticks } = niceValueAxis(rawMax, 5, r?.integer));
  }
  return {
    domain,
    ticks,
    tickFormatter: r?.tickFormatter ?? left?.tickFormatter,
    unit: r?.unit ?? left?.unit,
  };
}
